import React, { useState, useEffect } from 'react';
import './index.scss';
import { Helmet } from 'react-helmet';
import _ from 'lodash';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { withTranslation } from 'react-i18next';
import TopDrugs from '../TopDrugs/index.jsx';
import RxCardSendCoupon from '../RxCard/RxCardSendCoupon';
import {
  PRESC_DESC_URL,
  PRESC_DESC_URL_ES,
  RXSENSE,
  NAVITUS
} from '../../constants/defaults';
import DiscountSavingsSection from './DiscountSavingSection';
import PharmacyList from '../common/PharmacyList';
import { getStoreLdJson } from './ldJosn';
import Image from '../common/Image.jsx';
import Breadcrumb from '../DrugDetails/Breadcrumb';
import NotFound from '../NotFound/index.jsx';
import SkipTo from '../common/Tag/SkipTo';
import { viewCardData } from '../../services/Api/Coupon/newCouponService.js';

const { API } = require('../../config/' + process.env.REACT_APP_ENV);

const DiscountPage = ({ staticContext, location, match, t, i18n }) => {
  const [data, setData] = useState(null);
  const [cardImage, setCardImage] = useState(null);
  const [pharmacyName, setPharmacyName] = useState(null);
  const [selectedPharmacy, setSelectedPharmacy] = useState(null);
  const [hiddenParams, setHiddenParams] = useState({});

  useEffect(() => {
    let initialData;
    if (
      typeof window !== 'undefined' &&
      typeof __INITIAL_DATA__ !== 'undefined'
    ) {
      initialData = window.__INITIAL_DATA__;
      delete window.__INITIAL_DATA__;
    } else {
      initialData = staticContext.data;
    }

    setData(initialData);
    setPharmacyName(initialData.name);
    setSelectedPharmacy({
      PartnerUser: initialData.partnerId,
      PartnerPassword: initialData.partnerPassword,
      PharmacyName: initialData.name
    });
  }, [staticContext]);

  useEffect(() => {
    let intervalId = setInterval(() => {
      const prospectId = _.getCookie('prospect-id');
      if (prospectId && pharmacyName) {
        clearInterval(intervalId);
        fetchCardDetail(pharmacyName);
      }
    }, 200);

    return () => clearInterval(intervalId);
  }, [pharmacyName]);

  const fetchCardDetail = async pharmacy => {
    if (_.isEqual(pharmacy, 'invalid')) return;

    const pharmacy_obj = selectedPharmacy || {
      PartnerUser: data?.partnerId,
      PartnerPassword: data?.partnerPassword,
      PharmacyName: data?.name
    };

    try {
      const fetchCouponResp = await viewCardData(
        pharmacy_obj.PartnerUser,
        pharmacy_obj.PartnerPassword,
        pharmacy_obj.PharmacyName
      );

      setCardImage(fetchCouponResp.pdfUrl);
      setSelectedPharmacy(pharmacy_obj);
      triggerGTMEvent(fetchCouponResp);
    } catch (err) {
      console.log(err.message);
    }
  };

  const triggerGTMEvent = res => {
    if (!_.isEmpty(res)) {
      _.triggerGTMVirtualPageViewEvent({
        event: 'cardGenerated',
        authNumber: res.memberNumberFull,
        grpNumber: res.groupNumber,
        bin: res.bin,
        pcn: res.pcn,
        pbm: data?.internalPharmacy ? RXSENSE : NAVITUS
      });
      setHiddenParams({
        authNumber: res.memberNumberFull,
        grpNumber: res.groupNumber,
        bin: res.bin,
        pcn: res.pcn,
        pharmacyName: pharmacyName
      });
    }
  };

  if (
    _.isEqual(pharmacyName, undefined) ||
    _.isEqual(pharmacyName, 'invalid') ||
    (!API.ALLOW_NAVITUS && !data?.internalPharmacy)
  ) {
    return <NotFound />;
  }

  return (
    <main>
      <div className="homepage-new">
        <Helmet>
          <title>{data && data.metaTitle}</title>
          <meta name="description" content={data && data.metaDescription} />
          <script type="application/ld+json">
            {getStoreLdJson(pharmacyName)}
          </script>
          <link
            rel="canonical"
            href={`https://www.singlecare.com${location.pathname}`}
          />
          <link
            rel="alternate"
            href={`${PRESC_DESC_URL}/${match.params.storeType}`}
            hrefLang="en-us"
          />
          <link
            rel="alternate"
            href={`${PRESC_DESC_URL_ES}/${match.params.storeType}`}
            hrefLang="es-us"
          />
          <meta property="og:title" content={data && data.ogTitle} />
          <meta
            property="og:description"
            content={data && data.ogDescription}
          />
          <meta
            property="og:url"
            content={`https://www.singlecare.com${location.pathname}`}
          />
          <meta property="og:site_name" content="SingleCare" />
          <meta property="og:type" content="website" />
          <meta
            property="og:image"
            content="https://www.singlecare.com/content/images/homepage/singleCareLogo.jpg"
          />
        </Helmet>
        <div className="DiscountPageContainer">
          <div className="LandingSearch">
            <Breadcrumb>
              <span>
                <a href={_.withLocale(i18n.language, '/')}>{t(`Home`)}</a>
              </span>
              <a
                href={_.withLocale(
                  i18n.language,
                  `/prescription-discount-card`
                )}
              >
                {t('Prescription Discount Card')}
              </a>
              {i18n.language === 'en' ? (
                <span>{`${pharmacyName} ${t('Card')}`}</span>
              ) : (
                <span>
                  {`En ${pharmacyName} Pharmacy`}&nbsp;
                  {t('Card')}
                </span>
              )}
            </Breadcrumb>
          </div>
          <div className="DiscountPageContent">
            <SkipTo>
              <h1>{data && data.h1}</h1>
            </SkipTo>
            <h2>{data && data.h2}</h2>
            <div className="CardMasheadDetails">
              <div className="card-masthead--image">
                <Image
                  imageUrl={cardImage}
                  imageAlt={_.getNewPharmacyALTName(hiddenParams)}
                  imageClass="contactImage"
                  containerClass="imageContainer"
                  loaderClass="loaderImage"
                  loaderalt={t('Prescription card loading')}
                />
              </div>
              {data && data.cardHighlights && (
                <div className="card-masthead-description">
                  {documentToReactComponents(JSON.parse(data.cardHighlights))}
                </div>
              )}
            </div>
            <RxCardSendCoupon
              selectedPrice={selectedPharmacy}
              isRxCard={true}
              isFromDiscountPage={true}
              isFromPhysicalModal={false}
              ispharmacySpecificPage={true}
            />
          </div>
          <div className="topDrugsSection">
            <h2 className="subTitle">
              {data && data.popularPrescriptionsSubhead}
            </h2>
            <TopDrugs drugsObj={{ drugs: data?.popularPrescriptions?.drugs }} />
            <div>
              <section className="drug-faqs">
                <div className="faq-list">
                  <h2>{data && data.faqSubhead}</h2>
                  <div className="faq-art">
                    <div className="faq-art-b">
                      {data &&
                        data.faQs &&
                        documentToReactComponents(JSON.parse(data.faQs))}
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>
          <div>
            <DiscountSavingsSection
              isSinglecare={false}
              pharmacyName={pharmacyName}
            />
            <div className="unlockPrescriptionContainer">
              <h2>{data && data.cardSelectorSubhead}</h2>
              <PharmacyList
                cardSelectorPharmacies={data && data.cardSelectorPharmacies}
              />
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default withTranslation()(DiscountPage);
